import React, { useContext, useEffect, useMemo, useState } from "react"
import FormBuilder from "@app/components/FormBuilder"
import { useFetcher, useLocation, useParams, useRouteLoaderData } from "react-router-dom"
import { useWizardSteps } from "@app/lib/hooks/useWizardSteps"
import { getCompanyDisplay } from "@app/utils/inspections"
import FormNavigationButtons from "@app/components/FormNavigationButtons/FormNavigationButtons"
import Config from "@app/constants/Config"
import { getVisibleFieldCompletion } from "@app/utils/form"
import { filterElements } from "@app/utils/helper"
import { SaveInspectionContext } from "@app/components/SaveInspectionContextContainer/SaveInspectionContextContainer.jsx"
import InspectionFormObserver from "@app/components/inspection-form/InspectionFormObserver.jsx"
import Hint from "@app/components/Hint"
import { Row, Container, Col } from "react-bootstrap"
import { SignsOverlayContext } from "@app/components/SignsOverlayContext/SignsOverlayContext"
import SignsOverlay from "@app/components/SignsOverlay"
import AlertContextProvider from "@app/components/AlertContext"

export const useFormStepConfig = (signsStep = false) => {
	const routeLoaderData = useRouteLoaderData("inspection")
	let { inspectionConfig, inspection } = routeLoaderData || {}

	const { office, jobs } = useRouteLoaderData("root") || {}
	const params = useParams()
	const location = useLocation()

	if (!routeLoaderData) return null

	const step = parseInt(params.step, 10) || 1
	const formStep = !signsStep
		? inspectionConfig?.forms?.steps?.[step - 1]
		: inspectionConfig?.forms?.signsOverlayStep

	formStep?.fieldsets?.map((fieldset) => {
		fieldset.elements = fieldset?.elements || []
	})
	const flattenedElements = formStep?.fieldsets?.flatMap(({ elements }) => elements)

	formStep.followUp = inspection?.type === "FOLLOW_UP" || location.pathname.startsWith("/inspection/new-follow-up/")

	// set customer options in select // TODO nicht nach name filtern
	const clientElement = flattenedElements.find(
		({ name }) => name !== undefined && name === "kundenundeinsatzort_kunde_kunde"
	)
	if (clientElement) {
		clientElement.options = office?.kundenliste
			.filter(({ Status }) => !Status || String(Status).toLowerCase() !== "inaktiv")
			.sort((a, b) => (a.KundenName < b.KundenName ? -1 : 1))
			.map(({ KUNDENNR, KundenName }) => ({
				label: `${KundenName} (${KUNDENNR})`,
				name: KundenName,
				value: String(KUNDENNR),
				selected: false
			}))
	}

	flattenedElements
		.filter(
			({ name }) => name.startsWith("kundenundeinsatzort_kunde_") && name !== "kundenundeinsatzort_kunde_kunde"
		)
		.forEach((entry) => {
			entry.disabled = true
			entry.required = true
		})

	const jobElement = flattenedElements.find(
		({ name }) => name !== undefined && name === "taetigkeit_beschreibung_ueberlassungals"
	)
	if (jobElement) {
		jobElement.elementType = "jobSelector"
		jobElement.jobs = jobs
	}

	return formStep
}

const observers = [
	InspectionFormObserver
]

function FormStepNavigation ({ formikProps, formikFields, steps, inspection, onPrevious, onSubmit, type }) {
	// formikFields is FormBuilder's live, observer-mutated field list — using it (rather than the
	// static step config) means a field an observer disables/hides at runtime is reflected here too
	const relevantFields = (formikFields || []).filter((element) => filterElements(element, type))
	const hasDangerField = relevantFields.some(
		({ name }) => name === "physischegefaehrdungsanalyse_gefaehrdungen_gefaehrdungen"
	)
	const { hasUnfilledRequired, hasUnfilledOptional } = getVisibleFieldCompletion(relevantFields, formikProps?.values)
	return <FormNavigationButtons
		steps={steps}
		path={`${Config.BASE_PATH}/inspection/${inspection?.uuid}`}
		formikProps={formikProps}
		onPrevious={onPrevious}
		onSubmit={onSubmit}
		hasDangerField={hasDangerField}
		hasUnfilledRequired={hasUnfilledRequired}
		hasUnfilledOptional={hasUnfilledOptional}
	/>
}

function NavigationError ({ fetcherData }) {
	const [error, setError] = useState(null)
	const location = useLocation()

	useEffect(() => {
		setError(null)

	}, [location])

	useEffect(() => {
		const hasError = fetcherData?.type === "FORM_ERROR"
		setError(hasError ? fetcherData : null)

	}, [fetcherData])

	return error
		? <Container>
			<Row>
				<Col className="pt-30">
					<Hint
						label={"Bitte füllen Sie zum Fortfahren die markierten Felder aus."} />
				</Col>
			</Row>
		</Container>
		: null
}

export default function FormStepContainer ({ inspection, newType }) {
	const { setSaveInspectionHandler } = useContext(SaveInspectionContext)
	const { setSignsOverlayHandler } = useContext(SignsOverlayContext)
	const [showSignsOverlay, setShowSignsOverlay] = useState(false)
	const fetcher = useFetcher()
	const signsFormStep = useFormStepConfig(true)
	const steps = useWizardSteps()
	const type = newType || inspection?.type || "INITIAL"
	const isFollowUp = (inspection?.type === "INITIAL" && inspection?.status === "COMPLETED") ||
		type === "FOLLOW_UP"
	const formStep = useFormStepConfig(false, isFollowUp)

	// follow-ups (neu UND beim bearbeiten einer bestehenden): firmenname aus den
	// inspektionsdaten (company_name/company_id) setzen, aufgelöst über die
	// kundenliste der niederlassung - so wird bei kundenumzügen der aktuelle
	// kunde statt einer überführten alten nummer angezeigt
	const { office } = useRouteLoaderData("root") || {}
	const values = useMemo(() => {
		if (type === "FOLLOW_UP") {
			const { name, id } = getCompanyDisplay(inspection, office)
			if (name && id !== undefined) {
				return {
					...inspection.values,
					kundenundeinsatzort_kunde_kunde: `${name} (${id})`,
					kundenundeinsatzort_kunde_kunde__ID: id,
					kundenundeinsatzort_kunde_kunde__NAME: name,
				}
			}
		}
		return inspection?.values || {}
	}, [inspection, type, office])

	const onPrevious = (data) => {
		fetcher.submit(
			{ action: "PREVIOUS", json: JSON.stringify(data), type },
			{
				method: "post",
				encType: "application/x-www-form-urlencoded"
			}
		)
	}

	const onSubmit = async (data, navigateTarget = null) => {
		await fetcher.submit(
			{ action: navigateTarget ? "NAVIGATE" : "SUBMIT", json: JSON.stringify(data), navigateTarget, type },
			{
				method: "post",
				encType: "application/x-www-form-urlencoded"
			}
		)
	}

	const updateSaveHandler = (values, dirty) => {
		setSaveInspectionHandler({
			fn: async (navigateTarget) => {
				const res = await fetcher.submit(
					{
						action: navigateTarget ? "NAVIGATE" : "SAVE",
						json: JSON.stringify(values),
						navigateTarget,
						type,
					},
					{
						method: "post",
						encType: "application/x-www-form-urlencoded"
					}
				)
				setSaveInspectionHandler({
					fn: null,
					active: false,
				})
			},
			active: dirty,
		})
	}

	const onChange = ({ formikProps }) => {
		const { values, dirty } = formikProps
		updateSaveHandler(values, dirty)
	}

	useEffect(() => {
		// initial setup
		updateSaveHandler(values, false)
		setSignsOverlayHandler({
			enabled: true,
			onClick: () => {
				setShowSignsOverlay(!showSignsOverlay)
			},
		})
	}, [])

	return (
		<>
			<NavigationError fetcherData={fetcher?.data} />
			<SignsOverlay
				onSave={async (data) => {
					setShowSignsOverlay(false)
					await fetcher.submit(
						{ action: "SAVE", json: JSON.stringify(data), type },
						// { action: "NAVIGATE", json: JSON.stringify(data), navigateTarget: location.pathname },
						{
							method: "post",
							encType: "application/x-www-form-urlencoded"
						}
					)
					// inspection.values = {...data}
				}}
				fieldsets={signsFormStep.fieldsets}
				onCancel={() => { setShowSignsOverlay(false) }}
				show={showSignsOverlay}
				values={values}
				header={signsFormStep.header}
			/>
			<AlertContextProvider>
				<FormBuilder
					id={"inspection-form"}
					key={inspection !== null ? `${inspection.uuid}-${steps?.current}` : "new-inspection"}
					values={values}
					onSubmit={onSubmit}
					onChange={onChange}
					header={formStep.header}
					subheader={formStep.subheader}
					fieldsets={formStep.fieldsets}
					isFollowUp={isFollowUp}
					Navigation={({ formikProps, formikFields }) => (
						<FormStepNavigation
							steps={steps}
							inspection={inspection}
							formikProps={formikProps}
							formikFields={formikFields}
							onPrevious={onPrevious}
							onSubmit={onSubmit}
							type={type}
						/>
					)}
					instantErrors={fetcher?.data?.errors}
					observers={observers}
				/>
			</AlertContextProvider>
		</>
	)
}
